import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import InvoiceTable from '@accounting/components/tables/invoice-table/invoice-table.vue';
import OrderTable from '@accounting/components/tables/order-table/order-table.vue';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider';

@Component({
  name: 'bpc-credit-note-container',
  components: {
    'bpc-invoice-table': InvoiceTable,
    'bpc-order-table': OrderTable,
  },
})
export default class CreditNoteContainer extends Vue {
  $refs: {
    orderTable: InstanceType<typeof OrderTable>;
    invoiceTable: InstanceType<typeof InvoiceTable>;
  };

  // Selects the table to show 'orders' || 'invoices'
  public tableToShow = null;
  //Filter used in the tableToShow
  public filterConfigName: string = '';
  // Extended filters only used in orders at the moment
  public extendedFilterConfigName: string = '';
  // Filter fields to exclude of the table
  public filtersExcluded: string[] = [];
  // Title of the table to show
  public tableTitle = 'global.models.invoice.heading';
  // Subtitle of the table to show
  public tableSubtitle = 'global.models.invoice.subheading';
  // Set the type of accounting journals that shows the generate invoice Form and validate invoice form.
  public accountingJournalsType: string[] = ['credit_note'];
  // Enable/disable  actions. (Disabled by default)
  public validateInvoicePageAction: boolean = null;
  public sendInvoiceAction: boolean = null;
  public syncInvoicePageAction: boolean = null;
  public syncPaymentPageAction: boolean = null;
  public bulkMoveToTrashPageAction: boolean = null;
  public markInvoicesAsPaidPageAction: boolean = true;
  // Create invoice form config name
  public formConfigName: string = 'create-credit-note-form';
  public displayConfigName: string = 'credit-notes-table';

  // LIFECYCLE
  mounted() {
    this.onChangeRoute();
  }

  // WATCHERS
  @Watch('$route.name')
  onChangeRoute() {
    switch (this.$route.name) {
      case 'admin-orders-to-credit':
        this.tableToShow = 'orders';
        this.filterConfigName = 'orders-to-credit';
        this.extendedFilterConfigName = 'orders-to-invoice-extended';
        this.filtersExcluded = ['status', 'hasNoInvoice', 'onlyNegativePrice', 'needToBeInvoiced'];
        this.tableTitle = 'accounting.credit_notes.tabs.orders_to_credit.title';
        this.tableSubtitle = 'accounting.credit_notes.tabs.orders_to_credit.subtitle';
        break;
      case 'admin-credit-notes-in-draft':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'draft-credit-notes';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.credit_notes.tabs.credit_notes_in_draft.title';
        this.tableSubtitle = 'accounting.credit_notes.tabs.credit_notes_in_draft.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = true;
        this.sendInvoiceAction = false;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = true;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'credit-notes-draft';
        break;
      case 'admin-credit-notes-to-pay':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'to-pay-credit-notes';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.credit_notes.tabs.credit_notes_in_to_pay.title';
        this.tableSubtitle = 'accounting.credit_notes.tabs.credit_notes_in_to_pay.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = true;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = true;
        this.displayConfigName = 'credit-notes-table';
        break;
      case 'admin-credit-notes-paid-and-to-pay':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'credit-notes-paid-and-to-pay';
        this.filtersExcluded = ['type'];
        this.tableTitle = 'accounting.credit_notes.tabs.credit_notes_paid_and_to_pay.title';
        this.tableSubtitle = '';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = true;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = true;
        this.displayConfigName = 'credit-notes-table';
        break;
      case 'admin-credit-notes-paid':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'paid-credit-notes';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.credit_notes.tabs.credit_notes_in_paid.title';
        this.tableSubtitle = 'accounting.credit_notes.tabs.credit_notes_in_paid.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'credit-notes-table';
        break;
      case 'admin-credit-notes-to-sync':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'to-sync-credit-notes';
        this.filtersExcluded = ['status', 'type', 'has_invoice_sync_item'];
        this.tableTitle = 'accounting.credit_notes.tabs.credit_notes_to_sync.title';
        this.tableSubtitle = 'accounting.credit_notes.tabs.credit_notes_to_sync.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = false;
        this.syncInvoicePageAction = true;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'credit-notes-to-sync';
        break;
      case 'admin-credit-notes-synchronized':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'synchronized-credit-notes';
        this.filtersExcluded = ['status', 'type', 'has_invoice_sync_item'];
        this.tableTitle = 'accounting.credit_notes.tabs.credit_notes_synchronized.title';
        this.tableSubtitle = 'accounting.credit_notes.tabs.credit_notes_synchronized.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = true;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'credit-notes-synced';
        break;
      case 'admin-credit-notes-trashed':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'trashed-credit-notes';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.credit_notes.tabs.credit_notes_trashed.title';
        this.tableSubtitle = 'accounting.credit_notes.tabs.credit_notes_trashed.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = false;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'invoices-trashed';
        break;
      default:
        break;
    }
  }
}
